import { Spin } from '@m4b-design/components';
import { CODE_SUCCESS } from '@features/constants/network';
import { getQuery, setQuery } from '@i18n-ecom/lib/dist/commonjs/url';
import { useTranslation } from '@jupiter-app/plugin-i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import NoContentPage from '@features/components/no-content-page';
import { ErrorPage } from '@features/components/error-page';
import { useHistory, useLocation } from '@jupiter/plugin-runtime/router';
import { useContextUser, useRefCallback } from '@features/hooks';
import cx from 'classnames';
import Article from './article';
import styles from './index.scss';
import { ErrorCode } from './constants';
import {
  ContentsType,
  GetKnowledgeDetailData,
  RoleType,
  sellerLearningCenterApiClient,
} from '@/api/seller/serv/oec_seller_learning_center_api';
import { findPath } from '@/hooks/useFetchContents';
import ContentsTree from '@features/components/contents-tree';
import LoginWindow from '@features/components/login-window';
import { LoginPanel } from '@/components';
import { register } from '@/libs';
import { Path, ROLE_KEY } from '@/constants';
import qs from 'querystring';
import { useUserModel } from '@/models';
import { ContextUser } from '@features/hooks/useContextUser';
import { isAgreementPage } from '@/libs/url';
import { findTreeNodeById } from '@/utils/utils';
import { getPopReportQuery } from '@/hooks/usePopQuery';

const ErrorPageComponent = styled(ErrorPage)`
  position: relative;
  left: 0;
  right: 0;
`;

interface ErrorText {
  title: string;
  text: string;
  buttonText: string;
  buttonClick?: () => void;
}

const contentDetailReport = ({
  pathObjArr,
  user,
  learningID,
  learningName,
  category,
  isCreator,
  isSeller,
}: {
  pathObjArr: any[];
  user: ContextUser;
  learningID: string;
  learningName?: string;
  category?: ContentsType;
  isSeller?: boolean;
  isCreator?: boolean;
}) => {
  const isCourse = location.pathname.indexOf('course') > -1;
  const origin = getQuery(location.search, 'from')?.toString();
  const teaPartParams = {};
  const categoryMap: Record<ContentsType, string> = {
    [ContentsType.Faq_Details]: 'faq_details',
    [ContentsType.Policy_Center]: 'policy_center',
    [ContentsType.Feature_Guide]: 'feature_guide',
    [ContentsType.Courses]: 'courses',
    [ContentsType.Agreement_Center]: 'agreement_center',
  };

  if (!pathObjArr.length) {
    return false;
  }
  if (isCourse) {
    teaPartParams['content_type'] = 'course';
    teaPartParams['course_id'] = pathObjArr[pathObjArr.length - 2]?.content_id;
    teaPartParams['course_name'] =
      pathObjArr[pathObjArr.length - 2]?.content_name;
  } else if (origin === 'policy') {
    teaPartParams['content_type'] = 'policy';
    teaPartParams['policy_id'] = pathObjArr[pathObjArr.length - 2]?.content_id;
    teaPartParams['policy_name'] =
      pathObjArr[pathObjArr.length - 2]?.content_name;
  } else {
    teaPartParams['guide_id'] = pathObjArr[pathObjArr.length - 2]?.content_id;
    teaPartParams['guide_name'] =
      pathObjArr[pathObjArr.length - 2]?.content_name;
  }
  const teaParams = Object.assign(teaPartParams, {
    page_name: 'content_detail',
    content_type: isCourse ? 'course' : 'essay',
    content_module: isAgreementPage()
      ? 'agreement'
      : category
        ? categoryMap[category]
        : '',
    learning_id: learningID,
    content_id: learningID,
    content_name: learningName,
    type: 'page',
    is_login: user.user_id ? '1' : '0',
    url: window.location.href,
    prev_page_name: window.document.referrer,
    is_video: false,
    identity_type: isCreator ? 'creator' : isSeller ? 'seller' : '',
  });
  return teaParams;
};

export interface EssayDetailPageProps {
  fromArticle?: boolean;
  onNoEssay?: () => void;
}

const EssayDetailPage: React.FC<EssayDetailPageProps> = React.memo(
  ({ onNoEssay }) => {
    const [loading, setLoading] = useState(true);
    // const [viewLoaded, setViewLoaded] = useState(false);
    const [empty, setEmpty] = useState(false);
    const [error, setError] = useState(false);
    const [needLogin, setNeedLogin] = useState(false);
    const [errorText, setErrorText] = useState<ErrorText>({
      title: '',
      text: '',
      buttonText: '',
    });
    const [user] = useContextUser();
    const { isLogin, isCreator, isSeller } = useUserModel();
    const [knowledge, setKnowledge] = useState<GetKnowledgeDetailData>();
    const { i18n } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const currentLanguage = useRef(i18n.language);
    const { t } = useTranslation();
    const [reportObj, setReportObj] = useState<any>();
    const [defaultOpenKeys, setDefaultOpenKeys] = useState<string[]>();
    const fromArticle =
      getQuery(location.search, 'from_article')?.toString() === '1';
    const knowledgeId = getQuery(location.search, 'knowledge_id')?.toString();
    const contentTypeCourse = 3;

    const updateReportObj = (
      obj: typeof reportObj,
      newKnowledgeId?: string,
    ) => {
      const { channel, ...search } = getQuery(location.search);

      if (newKnowledgeId || channel) {
        if (channel) {
          Object.assign(obj, { channel });
        }

        if (newKnowledgeId && search['knowledge_id'] !== newKnowledgeId) {
          search['knowledge_id'] = newKnowledgeId;
          delete search['from_article'];
        }

        history.replace({ search: qs.stringify(search as any) });
      }

      setReportObj(obj);
    };

    const fetchArticle = useRefCallback(() => {
      if (!knowledgeId) {
        setError(true);
        setLoading(false);
        return;
      }

      setLoading(true);

      const role_type = getQuery(location.search, ROLE_KEY)?.toString();
      const roleTypeNum =
        Number(role_type) ||
        { seller: RoleType.Seller, creator: RoleType.Creator }[role_type!] ||
        RoleType.Seller;
      const request = sellerLearningCenterApiClient.GetKnowledgeDetail({
        knowledge_id: Number(knowledgeId),
        role_type: roleTypeNum,
        ...(fromArticle ? { old_content_flag: 1 } : {}),
      });

      request.then(result => {
        if (result.code === CODE_SUCCESS && result.data) {
          setError(false);
          setEmpty(false);
          setNeedLogin(false);
          setKnowledge(result.data);
          const keyArr: any[] = [];
          const pathObjArr: any[] = [];
          const newKnowledgeId =
            result.data.cur_knowledge?.learning_id.toString() || knowledgeId;
          try {
            findPath(
              result.data?.contents?.sub_contents,
              Number(newKnowledgeId),
            ).forEach(item => {
              keyArr.push(String(item.content_id));
              pathObjArr.push(item);
            });
          } catch (e) {
            console.log(e);
            setError(true);
          }

          const reportParams = contentDetailReport({
            pathObjArr,
            user,
            learningID: newKnowledgeId,
            learningName: result.data.cur_knowledge?.name,
            category: result.data.category,
            isCreator,
            isSeller,
            ...getPopReportQuery(),
          });
          updateReportObj(
            reportParams,
            newKnowledgeId !== knowledge ? newKnowledgeId : undefined,
          );
          setDefaultOpenKeys(keyArr);
        } else if (
          result.code === ErrorCode.NoContent ||
          result.code === ErrorCode.ContentNeedSync ||
          result.code === ErrorCode.ContentMapNotRight
        ) {
          if (fromArticle) {
            onNoEssay?.();
            return;
          }
          setKnowledge({
            category: knowledge?.category,
            contents: knowledge?.contents,
          });
          setEmpty(true);
          setNeedLogin(false);
          setError(false);
        } else if (result.code === ErrorCode.ContentOnlyForLogin) {
          setNeedLogin(true);
          setEmpty(false);
          setKnowledge({
            category: knowledge?.category,
            contents: knowledge?.contents,
          });
        } else {
          setErrorText({
            title: t('academy_load_fail_page_title'),
            text: t('academy_load_fail_page_text'),
            buttonText: t('academy_load_fail_page_btn'),
            buttonClick: fetchArticle,
          });
          setError(true);
        }
        setLoading(false);
      });
    }, []);

    useEffect(() => {
      if (isLogin === -1) {
        return;
      }
      if (i18n.language !== currentLanguage.current) {
        setKnowledge(undefined);
        currentLanguage.current = i18n.language;
      }
      fetchArticle();
    }, [i18n.language, knowledgeId, isLogin]);

    useEffect(() => {
      if (!knowledge?.cur_knowledge) return;

      const { name, ai_summary, description, content, image } = knowledge.cur_knowledge;

      const fallbackDescription =
        ai_summary?.trim() ||
        description?.trim() ||
        (content?.trim()?.slice(0, 150) ?? '') ||
        'Learn more on our platform...';

      const values = {
        'og:title': name,
        'og:description': fallbackDescription,
        'og:image': image,
      };

      Object.entries(values).forEach(([key, value]) => {
        const tag = document.querySelector(`meta[name="${key}"]`);
        if (tag) {
          tag.setAttribute('content', value ?? '');
        }
      });
    }, [knowledge?.cur_knowledge]);

    const selectedMenuKey = useMemo(() => {
      return [knowledgeId ?? ''];
    }, [knowledgeId]);

    const onClickMenuItem = useCallback(
      (key: string) => {
        const contentObj = knowledge?.contents
          ? findTreeNodeById(knowledge?.contents, Number(key))
          : knowledge?.contents;
        const params = new URLSearchParams(location.search);
        params.delete('anchor_link');
        if (contentObj?.content_type === contentTypeCourse) {
          params.set('from', 'course');
          params.delete('knowledge_id');
          const search = setQuery(`?${params.toString()}`, 'learning_id', key);
          history.push({
            pathname: Path.CourseDetail,
            search,
          });
        } else {
          const search = setQuery(`?${params.toString()}`, 'knowledge_id', key);
          history.push({
            pathname: Path.Essay,
            search,
          });
        }
      },
      [knowledge?.contents],
    );

    return (
      <div className={styles.articleDetailLayout}>
        {knowledge?.contents?.sub_contents && defaultOpenKeys?.length && (
          <ContentsTree
            className={cx(styles.menuLayout, 'h5:hidden')}
            selectedKeys={selectedMenuKey}
            defaultOpenKeys={defaultOpenKeys}
            onClickMenuItem={onClickMenuItem}
            contents={knowledge?.contents?.sub_contents}
            menuPage="seller_university_essay_detail_page"
            menuModule="seller_university_essay_detail"
          />
        )}
        <div className={`w-1 bg-neutral-line2 my-12`} />
        <section
          className={cx(
            styles.contentWrapper,
            error || loading || empty ? 'rounded-8' : '',
          )}>
          <Content
            errorText={errorText}
            knowledge={knowledge}
            contentId={Number(selectedMenuKey[0])}
            loading={loading}
            error={error}
            empty={empty}
            needLogin={needLogin}
            reportObj={reportObj}
            fetchArticle={fetchArticle}
          />
        </section>
      </div>
    );
  },
);

interface ContentProps {
  errorText: ErrorText;
  knowledge?: GetKnowledgeDetailData;
  contentId?: number;
  needLogin?: boolean;
  loading?: boolean;
  error?: boolean;
  empty?: boolean;
  reportObj?: object;
  fetchArticle?: () => void;
}

export const Content: React.FC<ContentProps> = props => {
  const {
    errorText,
    knowledge,
    contentId,
    needLogin = false,
    loading = false,
    error = false,
    empty = false,
    reportObj,
  } = props;
  const { t } = useTranslation();
  const { open: openLoginPanel } = LoginPanel.useModal();
  if (loading) {
    return (
      <div className="w-full h-full flex justify-center items-center rounded-8">
        <Spin />
      </div>
    );
  } else if (empty) {
    return (
      <div className="h-full mx-auto flex flex-1 justify-center items-center bg-white rounded-8">
        <NoContentPage />
      </div>
    );
  } else if (needLogin) {
    return (
      <div className="h-full mx-auto py-16 flex flex-1 justify-center items-center bg-white rounded-8">
        <LoginWindow
          onLogin={openLoginPanel}
          onRegister={() => register(RoleType.Seller)}
        />
      </div>
    );
  } else if (error) {
    return (
      <div className="h-full mx-auto py-16 flex flex-1 justify-center items-center bg-white rounded-8">
        <ErrorPageComponent
          hasNewVersion={true}
          title={errorText.title}
          text={errorText.text}
          leftButton={{
            type: 'primary',
            text: errorText.buttonText,
            onClick: errorText.buttonClick,
            style: {
              color: 'white',
              backgroundColor: '#1d1d1d',
            },
          }}
        />
      </div>
    );
  } else {
    return (
      <>
        {knowledge && (
          <Article
            className={styles.articleContent}
            knowledge={knowledge}
            contentId={contentId}
            reportObj={reportObj}
          />
        )}
      </>
    );
  }
};

export default EssayDetailPage;
